/* Default Variants are hints; an exact, server-verified Variant deep link is explicit. */
(() => {
  if (window.AndCharlieSizeIntent) return;
  window.AndCharlieSizeIntent = class {
    constructor(root) {
      this.root = root;
      this.size = null;
      const requestedId = new URL(window.location.href).searchParams.get('variant');
      const linkedVariant = this.variant();
      const sizeGroup = this.sizeGroup();
      const sizePosition = Number(sizeGroup?.dataset.acOptionPosition);
      // Keep an explicit deep link aligned with its image, price and Offer.
      // Invalid IDs and Shopify's first-available fallback never confirm a size.
      if (/^[1-9]\d*$/.test(requestedId || '') && String(linkedVariant?.id) === requestedId &&
          sizePosition > 0 && this.selected(sizeGroup)?.value === linkedVariant.options?.[sizePosition - 1]) {
        this.size = linkedVariant.options[sizePosition - 1];
      }
      this.fallback = new Map();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.onHistory = () => {
        this.reset();
        // Browsers can restore radio/select state after pageshow/popstate.
        // Clear that restored hint too, without discarding a new native choice.
        setTimeout(() => { if (!signal.aborted && this.size === null) this.apply(); }, 0);
      };
      // Clear before the browser snapshots this page's form state for history.
      window.addEventListener('pagehide', this.onHistory, { signal });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted || this.size === null) this.onHistory();
      }, { signal });
      window.addEventListener('popstate', this.onHistory, { signal });
      root.addEventListener('submit', (event) => {
        if (!event.target.closest('product-form') || this.confirmed()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.focusSize();
      }, { capture: true, signal });
      this.apply();
    }
    groups() { return Array.from(this.root.variantSelectors?.querySelectorAll('[data-ac-option-position]') || []); }
    sizeGroup() { return this.root.querySelector('variant-selects [data-ac-size-option]'); }
    selected(group) { return group.querySelector('input:checked, select option:checked'); }
    variant() {
      try { return JSON.parse(this.root.querySelector('variant-selects [data-selected-variant]')?.textContent); }
      catch { return null; }
    }
    matches(variant) {
      const position = Number(this.sizeGroup()?.dataset.acOptionPosition);
      return this.size !== null && position > 0 && variant?.options?.[position - 1] === this.size;
    }
    accepts(id) {
      const variant = this.variant();
      return !['loading', 'error'].includes(this.root.dataset.variantState) &&
        this.matches(variant) && String(variant.id) === String(id);
    }
    confirmed() { return this.accepts(this.variant()?.id); }
    prepare(event, target) {
      const group = target.closest('[data-ac-option-position]');
      // Native pointer/keyboard changes express intent. Server fallback and
      // programmatic changes must not promote a provisional Variant to intent.
      if (group?.hasAttribute('data-ac-size-option')) {
        if (event.isTrusted && target.value) this.size = target.value;
        else if (this.size !== target.value) this.size = null;
      }
      return this.groups().map((g) => {
        const position = Number(g.dataset.acOptionPosition);
        const selected = this.selected(g);
        const id = selected?.dataset.optionValueId || this.fallback.get(position);
        return { position, id };
      }).sort((a, b) => a.position - b.position).map(({ id }) => id).filter(Boolean);
    }
    confirmRecommendation(event, input) {
      const button = event.currentTarget;
      if (!event.isTrusted || event.type !== 'click' ||
          !button?.matches('[data-ac-fit-choose]') ||
          !this.root.querySelector('andcharlie-product-size-dialog')?.panel?.contains(button) ||
          !this.sizeGroup()?.contains(input) || button.dataset.acFitChoose !== input.value ||
          input.dataset.acCombination !== 'available' ||
          ['loading', 'error'].includes(this.root.dataset.variantState)) return false;
      this.size = input.value;
      return true;
    }
    clearSize() {
      // Dawn briefly retains the previous picker for its view transition.
      // Clear it too so history never captures its former checked size.
      for (const group of this.root.querySelectorAll('variant-selects [data-ac-size-option]')) {
        group.querySelectorAll('input[type=radio]').forEach((input) => { input.checked = false; });
        const select = group.querySelector('select');
        if (select) {
          let placeholder = select.querySelector('[data-ac-size-placeholder]');
          if (!placeholder) {
            placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'サイズを選択してください';
            placeholder.dataset.acSizePlaceholder = '';
            placeholder.disabled = true;
            select.prepend(placeholder);
          }
          select.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'));
          placeholder.selected = true;
          placeholder.setAttribute('selected', 'selected');
        }
        const selectedLabel = group.querySelector('[data-selected-value]');
        if (selectedLabel) selectedLabel.textContent = '';
      }
    }
    apply() {
      for (const group of this.groups()) {
        const selected = this.selected(group);
        if (selected?.dataset.optionValueId) this.fallback.set(Number(group.dataset.acOptionPosition), selected.dataset.optionValueId);
      }
      const variant = this.variant();
      // Preserve a chosen size through color changes, but never silently adopt
      // another size returned by Shopify or a programmatic product selection.
      if (!['loading', 'error'].includes(this.root.dataset.variantState) && this.size !== null && variant && !this.matches(variant)) this.size = null;
      const confirmed = this.confirmed();
      // An explicit size can form a combination Shopify does not sell. Keep
      // that intent, but never invent a Variant or call it an unselected size.
      const unavailable = this.root.dataset.variantState === 'ready' && this.size !== null &&
        this.selected(this.sizeGroup())?.value === this.size &&
        this.root.querySelector('variant-selects [data-selected-variant]')?.textContent.trim() === 'null';
      const pending = this.size !== null && ['loading', 'error'].includes(this.root.dataset.variantState);
      this.root.dataset.acSizeState = confirmed ? 'confirmed' : unavailable ? 'unavailable' : pending ? 'pending' : 'unconfirmed';
      if (unavailable) this.root.productForm?.toggleSubmitButton(true, 'この組み合わせはありません');
      if (this.size === null) this.clearSize();
      this.root.updateVariantInputs(confirmed ? variant.id : undefined);
      this.root.updateURL(this.root.dataset.url, confirmed ? variant.id : undefined);
      this.root.dispatchEvent(new CustomEvent('ac:size-intent', { bubbles: true }));
    }
    focusSize() {
      const group = this.sizeGroup();
      if (!group) return;
      group.setAttribute('tabindex', '-1');
      group.focus({ preventScroll: true });
      group.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    reset() {
      this.root.abortController?.abort();
      this.root.variantRequestId += 1;
      this.root.pendingRequestUrl = null;
      this.root.dataset.variantState = 'ready';
      this.root.removeAttribute('aria-busy');
      this.size = null;
      this.apply();
    }
    destroy() { this.controller.abort(); }
  };
})();
